// Platform-dispatched background removal. Web uses @imgly WASM; iOS uses the
// native Vision plugin. Returns the cut-out as a Blob, or null when there's no
// foreground subject. Throws on infra failure (retryable by the callers).
import { Capacitor } from '@capacitor/core';
import { removeBackground as imglyRemoveBackground } from '@imgly/background-removal';
import { StickerSegmenter } from './native-segmenter';

function base64PngToBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

// Hard cap on the native bridge call. Vision subject-lift is fast (well under a
// second), so any longer means the native side failed to respond (e.g. an
// unregistered plugin) — without this the JS promise would hang forever and the
// upload shimmer would spin indefinitely. Reject instead so the caller degrades
// to a plain photo.
const NATIVE_TIMEOUT_MS = 15000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export async function removeBackground(base64Image: string): Promise<Blob | null> {
  if (Capacitor.getPlatform() === 'ios') {
    // The plugin wants raw base64; strip any `data:<mime>;base64,` prefix.
    const raw = base64Image.replace(/^data:[^;]+;base64,/, '');
    const { cutoutBase64, subject } = await withTimeout(
      StickerSegmenter.removeBackground({ imageBase64: raw }),
      NATIVE_TIMEOUT_MS,
      'Native sticker segmentation',
    );
    if (subject === false || !cutoutBase64) return null;
    return base64PngToBlob(cutoutBase64);
  }
  // web (and anything else routed here): @imgly always returns a cut-out Blob.
  return imglyRemoveBackground(base64Image);
}
